import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Theme,
  Grid,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import UploadForm from './UploadForm'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import UploadView from './UploadView'
import { getIpdEcgReports } from '../../../../../services/TricogService'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getEcgFile } from '../../../../opd/components/devices/Constant'

dayjs.extend(utc)
dayjs.extend(timezone)

const styles = (theme: Theme) => ({
  tricogContainer: {
    '.MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
    },
  },
  createBtn: {
    borderRadius: 8,
    alignSelf: 'center',
    fontWeight: 600,
    textTransform: 'none',
    background: '#4682b4',
  },
  tableHead: {
    backgroundColor: '#4682b4',
  },
  tableHeadCell: {
    color: '#ffffff !important',
  },
  dialogCloseBtn: {
    position: 'absolute',
    right: 19,
    top: 15,
    color: theme.palette.grey[500],
  },
  // tableRoot: {
  //   width: 'fit-content',
  //   minWidth: '50%',
  //   [theme.breakpoints.down('sm')]: {
  //     minWidth: '100% !important',
  //   },
  // },
})

const TricogIpd = ({ patientRegisteredId }: { patientRegisteredId: any }) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const [rowData, setRowData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [openUpload, setOpenUpload] = useState(false)
  const [openView, setOpenView] = useState(false)
  const [selectedRow, setSelectedRow] = useState({})

  const handleView = async (row: any) => {
    setIsLoading(true)
    const fileData = await getEcgFile(row?.ecgReportFileName)
    setSelectedRow({
      ...row,
      ...fileData, // contains pdfFile and pdfFileUrl
    })
    setIsLoading(false)
    setOpenView(true)
  }

  const handleUpload = async (row: any) => {
    setIsLoading(true)
    const fileData = await getEcgFile(row?.ecgReportFileName)
    setSelectedRow({
      ...row,
      ...fileData, // contains pdfFile and pdfFileUrl
    })
    setIsLoading(false)
    setOpenUpload(true)
  }

  // const handleCloseView = () => {
  //   setOpenView(false)
  //   setSelectedRow({})
  // }

  const handleCloseUpload = () => {
    setOpenUpload(false)
    setSelectedRow({})
  }

  const handleOpenUpload = () => {
    setSelectedRow({})
    setOpenUpload(true)
  }

  const getRowData = useCallback(async () => {
    setIsLoading(true)

    const response: any = await getIpdEcgReports(patientRegisteredId)

    if (response?.success) {
      setRowData(response?.data)
    } else {
      setRowData([])
      toast.error(response?.message)
    }
    setIsLoading(false)
  }, [patientRegisteredId])

  const handleAfterSubmit = () => {
    handleCloseUpload()
    getRowData()
  }

  useEffect(() => {
    getRowData()
  }, [getRowData])

  return (
    <Grid container spacing={2}>
      <Dialog
        open={openUpload}
        onClose={handleCloseUpload}
        maxWidth='md'
        fullWidth
      >
        <DialogContent>
          <IconButton
            aria-label='close'
            onClick={handleCloseUpload}
            className={classes?.dialogCloseBtn}
          >
            <CloseIcon />
          </IconButton>
          <UploadForm
            screen='ipd'
            data={selectedRow}
            onSubmit={handleAfterSubmit}
            muhId={patientRegisteredId}
          />
        </DialogContent>
      </Dialog>
      <Grid
        item
        xs={12}
        sm={12}
        md={7}
        lg={7}
        xl={7}
        className={classes?.tricogContainer}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          mb={2}
          width={'100%'}
          // minWidth={{
          //   xs: '100%',
          //   sm: '100%',
          //   md: '50%',
          //   lg: '50%',
          //   xl: '50%',
          // }}
          // width={'fit-content'}
        >
          <Typography variant='h6'>Tricog Dashboard</Typography>
          <Button
            variant='contained'
            size='small'
            className={classes?.createBtn}
            onClick={handleOpenUpload}
            disabled={isLoading}
          >
            Add
          </Button>
        </Box>

        <Table size='small'>
          <TableHead className={classes?.tableHead}>
            <TableRow>
              <TableCell className={classes?.tableHeadCell} align='center'>
                Sl No
              </TableCell>
              <TableCell className={classes?.tableHeadCell}>Date</TableCell>
              <TableCell className={classes?.tableHeadCell}>Time</TableCell>
              <TableCell className={classes?.tableHeadCell}>
                Doctor/Nurse
              </TableCell>
              <TableCell className={classes?.tableHeadCell} align='center'>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  Loading...
                </TableCell>
              </TableRow>
            ) : rowData?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              rowData?.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell align='center'>{index + 1}</TableCell>
                  <TableCell>
                    {row?.createdOn
                      ? dayjs(row?.createdOn).format('MMMM D, YYYY')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {' '}
                    {row?.createdOn
                      ? dayjs
                          .utc(row?.createdOn) // parse as UTC
                          .tz(dayjs.tz.guess()) // convert to local timezone
                          .format('hh:mm A') // format as 12-hour time with AM/PM
                      : '-'}
                  </TableCell>
                  <TableCell>{row?.name || '-'}</TableCell>
                  <TableCell align='center'>
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      flexDirection={'row'}
                      gap={3}
                    >
                      <IconButton
                        size='small'
                        onClick={() => handleUpload(row)}
                      >
                        <Tooltip title='Edit'>
                          <EditOutlinedIcon
                            fontSize='small'
                            htmlColor='#4682b4'
                          />
                        </Tooltip>
                      </IconButton>
                      <IconButton size='small' onClick={() => handleView(row)}>
                        <Tooltip title='View'>
                          <VisibilityOutlinedIcon
                            fontSize='small'
                            htmlColor='#4682b4'
                          />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
        {openView && !openUpload && Object.keys(selectedRow)?.length > 0 && (
          <UploadView data={selectedRow} />
        )}
      </Grid>
    </Grid>
  )
}

export default TricogIpd
